import { useApolloClient } from "@apollo/client";
import {
  ModalOpenProcess,
  ModalOpenProcessRef,
  useScreenWidth,
} from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { QuotePdfCalculationType } from "../../../../clients/graphqlTypes.js";
import {
  GetAddressProcess,
  GetAddressProcessRef,
} from "../../addresses/GetAddressProcess.js";
import { PdfDownloadArgs } from "../hooks/useDownloadQuotePdf.js";
import { PublishQuoteAndSendViaEmailModal_ProjectFragment } from "../modals/PublishQuoteAndSendViaEmailModal.generated.js";
import { PublishQuoteAndSendViaEmailModal } from "../modals/PublishQuoteAndSendViaEmailModal.js";
import {
  PublishQuoteButton_QuoteFragment,
  usePublishQuoteButton_SetContractorAddressMutation,
} from "./PublishQuoteButton.generated.js";
import { Button } from "@mui/material";

interface Props {
  docId: string;
  projectId: string;
  disabled?: boolean;
  project: PublishQuoteAndSendViaEmailModal_ProjectFragment;
  doc: PublishQuoteButton_QuoteFragment;
  handleComplete?: () => Promise<unknown> | unknown;
  calculationType: QuotePdfCalculationType;
  pdfTemplateArgs?: PdfDownloadArgs | null;
}

export const PublishQuoteButton = ({
  docId,
  projectId,
  disabled = false,
  project,
  doc,
  handleComplete,
  calculationType,
  pdfTemplateArgs,
}: Props) => {
  const { t } = useTranslate([
    "QuoteEdit",
    "QuoteSend",
    "OrganisationProfile",
    "Global",
  ]);
  const { isMaxPhone } = useScreenWidth();

  const processRef = React.useRef<ModalOpenProcessRef>();
  const getAddressProcessRef = React.useRef<GetAddressProcessRef>(null);

  const client = useApolloClient();
  const [setContractorAddress] =
    usePublishQuoteButton_SetContractorAddressMutation({ client });

  const handlePublishQuote = async () => {
    if (!doc.contractor.address) {
      const address = await getAddressProcessRef.current?.getAddress({
        id: "system-organisation-billing-address-modal",
        inputLabel: t("Billing address", { ns: "OrganisationProfile" }),
        description: t(
          "Please provide your organisation billing address in order to publish a quote",
          { ns: "QuoteEdit" }
        ),
      });
      if (!address) return;
      await setContractorAddress({
        variables: {
          input: {
            projectId,
            docId,
            docActorType: "CONTRACTOR",
            docActorId: doc.contractor.id,
            docActor: { address },
          },
        },
      });
    }
    processRef.current?.open();
  };

  return (
    <>
      <Button
        color="primary"
        variant="contained"
        disabled={disabled}
        onClick={handlePublishQuote}
      >
        {isMaxPhone
          ? t("Publish", { ns: "Global" })
          : t("Publish doc", { ns: "QuoteEdit" })}
      </Button>
      <GetAddressProcess ref={getAddressProcessRef} />
      <ModalOpenProcess
        ref={processRef}
        Modal={PublishQuoteAndSendViaEmailModal}
        modalProps={{
          quote: doc,
          projectId,
          project,
          quoteId: docId,
          calculationType,
          pdfTemplateArgs,
          handleComplete: async handleClose => {
            await handleComplete?.();
            handleClose();
          },
        }}
      />
    </>
  );
};
